import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { GroupSummaryResponse } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { groupsApi } from '@/services/groups.api';
import { ApiError } from '@/services/api-client';
import { Alert, EmptyState, Spinner } from '@/components/ui';

/**
 * The cell structure as an org chart: every group that reports into another
 * one, nested under it — the shape a network of home groups actually has,
 * which a flat directory can't show.
 */
export function GroupsHierarchyPage(): ReactNode {
  const { t } = useI18n();

  const query = useQuery({
    queryKey: ['groups', 'hierarchy'],
    // Same 100-group ceiling the map view accepts — see its own note.
    queryFn: ({ signal }) => groupsApi.list({ limit: 100 }, signal),
  });

  const groups = query.data?.data ?? [];

  const childrenByParent = useMemo(() => {
    const map = new Map<string | null, GroupSummaryResponse[]>();
    for (const group of groups) {
      const key = group.parentGroupId;
      const siblings = map.get(key);
      if (siblings) siblings.push(group);
      else map.set(key, [group]);
    }
    return map;
  }, [groups]);

  const roots = childrenByParent.get(null) ?? [];

  return (
    <div className="page">
      <Link to="/groups" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <div>
          <h1 className="page-title">{t('groups.group.hierarchyTitle')}</h1>
        </div>
      </header>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && roots.length === 0 && (
        <EmptyState title={t('groups.group.hierarchyEmpty')} />
      )}

      {roots.length > 0 && (
        <div className="group-tree">
          <GroupTreeLevel groups={roots} childrenByParent={childrenByParent} depth={0} />
        </div>
      )}
    </div>
  );
}

/**
 * One generation of the chart. Recurses into each node's own children;
 * `depth` is a hard stop, not just a style hook — pre-existing bad data (this
 * app has never let a write create a cycle, but a chart that trusted that
 * completely would hang the tab the day it turned out to be wrong) cannot
 * make this render forever.
 */
function GroupTreeLevel({
  groups,
  childrenByParent,
  depth,
}: {
  groups: readonly GroupSummaryResponse[];
  childrenByParent: ReadonlyMap<string | null, GroupSummaryResponse[]>;
  depth: number;
}): ReactNode {
  const MAX_DEPTH = 12;

  return (
    <ul className={depth === 0 ? 'group-tree-roots' : 'group-tree-children'}>
      {groups.map((group) => {
        const children = depth < MAX_DEPTH ? (childrenByParent.get(group.id) ?? []) : [];
        return (
          <li key={group.id} className="group-tree-node">
            <Link to={`/groups/${group.id}`} className="group-tree-chip">
              <span className="group-tree-chip-name">{group.name}</span>
              <span className="group-tree-chip-count">
                {group.memberCount}
                {group.capacity !== null ? ` / ${group.capacity}` : ''}
              </span>
            </Link>
            {children.length > 0 && (
              <>
                <span className="group-tree-connector" aria-hidden="true" />
                <GroupTreeLevel
                  groups={children}
                  childrenByParent={childrenByParent}
                  depth={depth + 1}
                />
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}
